import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { Subject, takeUntil, forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AuthService, User } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { ListingsService, UserListing } from '../../services/listings.service';
import {
  WishlistService,
  WishlistSummary,
} from '../../services/wishlist.service';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';
import { countryCodes, CountryCode } from '../../utils/country-codes';
import * as lpn from 'google-libphonenumber';
import { TopUpComponent } from '../top-up/top-up.component';
import { MarketingService } from '../../services/marketing.service';

interface ExtendedUserListing extends UserListing {
  images: string[];
  currentImageIndex: number;
  preloadedImages?: Set<number>;
  isImageLoading?: boolean;
  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls?: boolean;
  controlsTimeout?: any;
  hasShownControls?: boolean;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;
}

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    TopUpComponent,
  ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private cancelProfileLoad$ = new Subject<void>();
  private isProcessingEmailChange = false;

  currentUser: User | null = null;
  editMode = false;
  changePasswordMode = false;
  profileForm: FormGroup;
  passwordForm: FormGroup;
  isSubmitting = false;
  successMessage = '';
  errorMessage = '';
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  showTopUpModal = false;
  countryCodes: CountryCode[] = countryCodes;
  selectedCountryCode: string = '+1';
  selectedCountry: CountryCode | undefined;
  private phoneUtil = lpn.PhoneNumberUtil.getInstance();
  emailChangeStep: 'initial' | 'verification' = 'initial';
  newEmail: string = '';
  verificationCode: string = '';
  recentListings: ExtendedUserListing[] = [];
  loadedImages: Set<string> = new Set();
  totalActiveListings = 0;
  isLoadingListings = false;
  wishlistCount = 0;
  wishlistSummary: WishlistSummary = { total: 0, by_category: {} };
  showEmailPreferencesModal = false;
  marketingConsent = false;
  isUpdatingPreferences = false;

  passwordPattern =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#.])[A-Za-z\d@$!%*?&#.]{8,}$/;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private listingsService: ListingsService,
    private wishlistService: WishlistService,
    private router: Router,
    private marketingService: MarketingService,
    private fb: FormBuilder
  ) {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      country_code: [this.selectedCountryCode, Validators.required],
      phone_number: [
        '',
        [Validators.pattern(/^\d+$/), this.phoneNumberValidator()],
      ],
    });

    this.passwordForm = this.fb.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: [
          '',
          [Validators.required, Validators.pattern(this.passwordPattern)],
        ],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
        if (user) {
          this.updateFormWithUserData();
          this.loadAllProfileData();
        } else {
          this.resetProfileData();
        }
      });

    this.wishlistService.wishlistSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((summary) => {
        this.wishlistSummary = summary;
        this.wishlistCount = summary.total;
      });

    this.profileForm.get('country_code')?.valueChanges.subscribe((value) => {
      this.selectedCountryCode = value;
      this.selectedCountry = this.countryCodes.find(
        (c) => c.dialCode === value
      );

      const phoneControl = this.profileForm.get('phone_number');
      phoneControl?.updateValueAndValidity();
    });
    this.loadMarketingPreferences();
  }

  ngOnDestroy(): void {
    this.cancelProfileLoad$.next();
    this.cancelProfileLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAllProfileData(): void {
    if (!this.authService.isLoggedIn()) {
      this.isLoadingListings = false;
      return;
    }

    this.cancelProfileLoad$.next();

    this.isLoadingListings = true;

    forkJoin({
      listings: this.listingsService.getAllUserListingsSorted(),
      wishlistSummary: this.wishlistService.getWishlistSummary(),
    })
      .pipe(
        takeUntil(this.cancelProfileLoad$),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingListings = false;
        })
      )
      .subscribe({
        next: (results) => {
          const activeListings = results.listings.filter(
            (listing) =>
              listing.status === 'active' || listing.status === 'published'
          );
          this.totalActiveListings = activeListings.length;
          this.wishlistSummary = results.wishlistSummary;
          this.wishlistCount = results.wishlistSummary.total;
        },
        error: (error) => {
          console.error('Error loading profile data:', error);
          this.recentListings = [];
          this.totalActiveListings = 0;
          this.wishlistCount = 0;
          this.wishlistSummary = { total: 0, by_category: {} };
        },
      });
  }

  private resetProfileData(): void {
    this.wishlistCount = 0;
    this.wishlistSummary = { total: 0, by_category: {} };
    this.recentListings = [];
    this.totalActiveListings = 0;
    this.isLoadingListings = false;
  }

  onImageLoad(imageUrl: string, listing?: ExtendedUserListing): void {
    this.loadedImages.add(imageUrl);
    if (listing) listing.isImageLoading = false;
  }

  navigateToWishlist(): void {
    this.router.navigate(['/wishlist']);
  }

  navigateToMyListings(): void {
    this.router.navigate(['/my-listings']);
  }

  navigateToTickets(): void {
    this.router.navigate(['/tickets']);
  }

  private loadMarketingPreferences(): void {
    if (!this.authService.isLoggedIn()) return;

    this.marketingService
      .getMarketingPreferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preferences) => {
          this.marketingConsent = preferences.marketing_consent;
        },
        error: (error) => {
          console.error('Error loading marketing preferences:', error);
          this.marketingConsent = false;
        },
      });
  }

  onMarketingConsentChange(): void {
    if (this.isUpdatingPreferences) return;

    this.isUpdatingPreferences = true;
    this.errorMessage = '';

    this.marketingService
      .updateMarketingPreferences(this.marketingConsent)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isUpdatingPreferences = false;
          this.successMessage = this.marketingConsent
            ? 'Successfully subscribed to marketing emails'
            : 'Successfully unsubscribed from marketing emails';

          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        },
        error: (error) => {
          this.isUpdatingPreferences = false;
          this.errorMessage =
            error.message || 'Failed to update email preferences';
          this.marketingConsent = !this.marketingConsent;
        },
      });
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      try {
        const regionCode = this.getRegionCode(this.selectedCountryCode);
        if (!regionCode) return { invalidNumber: true };

        const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
          control.value,
          regionCode
        );
        if (!this.phoneUtil.isValidNumber(phoneNumber)) {
          return { invalidNumber: true };
        }
        return null;
      } catch (e) {
        return { invalidNumber: true };
      }
    };
  }

  private getRegionCode(countryCode: string): string | null {
    const code = countryCode.replace('+', '');
    const country = this.countryCodes.find((c) => c.dialCode === countryCode);
    return country ? country.code : null;
  }

  getPhoneExampleNumber(): string {
    try {
      const regionCode = this.getRegionCode(this.selectedCountryCode);
      if (!regionCode) return '';

      const example = this.phoneUtil.getExampleNumber(regionCode);
      if (!example) return '';

      return this.phoneUtil.format(example, lpn.PhoneNumberFormat.NATIONAL);
    } catch (e) {
      return '';
    }
  }

  updateFormWithUserData(): void {
    if (this.currentUser && !this.isProcessingEmailChange) {
      let countryCode = '+1';
      let localNumber = '';

      if (this.currentUser.phone_number) {
        try {
          const phoneNumber = this.phoneUtil.parse(
            this.currentUser.phone_number
          );
          countryCode = '+' + phoneNumber.getCountryCode();

          localNumber = this.phoneUtil
            .format(phoneNumber, lpn.PhoneNumberFormat.NATIONAL)
            .replace(/[^0-9]/g, '');

          this.selectedCountryCode = countryCode;
          this.selectedCountry = this.countryCodes.find(
            (c) => c.dialCode === countryCode
          );
        } catch (e) {
          localNumber = this.currentUser.phone_number.replace(/^\+\d+/, '');
        }
      }

      this.profileForm.patchValue({
        firstName: this.currentUser.first_name,
        lastName: this.currentUser.last_name,
        email: this.currentUser.email,
        country_code: countryCode,
        phone_number: localNumber,
      });
    } else if (this.currentUser && this.isProcessingEmailChange) {
      let countryCode = '+1';
      let localNumber = '';

      if (this.currentUser.phone_number) {
        try {
          const phoneNumber = this.phoneUtil.parse(
            this.currentUser.phone_number
          );
          countryCode = '+' + phoneNumber.getCountryCode();

          localNumber = this.phoneUtil
            .format(phoneNumber, lpn.PhoneNumberFormat.NATIONAL)
            .replace(/[^0-9]/g, '');

          this.selectedCountryCode = countryCode;
          this.selectedCountry = this.countryCodes.find(
            (c) => c.dialCode === countryCode
          );
        } catch (e) {
          localNumber = this.currentUser.phone_number.replace(/^\+\d+/, '');
        }
      }

      this.profileForm.patchValue({
        firstName: this.currentUser.first_name,
        lastName: this.currentUser.last_name,
        country_code: countryCode,
        phone_number: localNumber,
      });
    }
  }

  formatPhoneNumber(): string | null {
    const countryCode = this.profileForm.get('country_code')?.value;
    const localNumber = this.profileForm.get('phone_number')?.value;

    if (!countryCode || !localNumber) {
      return null;
    }

    try {
      const regionCode = this.getRegionCode(countryCode);
      if (!regionCode) return `${countryCode} ${localNumber}`;

      const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
        localNumber,
        regionCode
      );
      return this.phoneUtil.format(phoneNumber, lpn.PhoneNumberFormat.E164);
    } catch (e) {
      return `${countryCode} ${localNumber}`;
    }
  }

  passwordMatchValidator(form: FormGroup): { [key: string]: boolean } | null {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;

    return newPassword && confirmPassword && newPassword !== confirmPassword
      ? { passwordMismatch: true }
      : null;
  }

  getPasswordStrength(password: string): number {
    if (!password) return 0;

    let strength = 0;

    if (password.length >= 8) {
      strength += 25;
    } else {
      strength += Math.min((password.length / 8) * 25, 20);
    }

    if (/[A-Z]/.test(password)) strength += 25;

    if (/\d/.test(password)) strength += 25;

    if (/[^a-zA-Z0-9]/.test(password)) strength += 25;

    return Math.min(strength, 100);
  }

  getPasswordStrengthText(password: string): string {
    const strength = this.getPasswordStrength(password);

    if (strength < 40) return 'Weak';
    if (strength < 70) return 'Medium';
    return 'Strong';
  }

  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    this.changePasswordMode = false;
    this.emailChangeStep = 'initial';

    if (this.editMode) {
      this.updateFormWithUserData();
    }

    this.clearMessages();
  }

  toggleChangePasswordMode(): void {
    if (this.currentUser?.is_oauth_user) {
      this.errorMessage =
        'You cannot change your password because you registered using OAuth. Password management is handled by your OAuth provider (e.g., Google).';
      setTimeout(() => this.clearMessages(), 5000);
      return;
    }
    this.changePasswordMode = true;
  }

  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  private initiateEmailChange(newEmail: string): void {
    this.userService.initiateEmailChange(newEmail).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.emailChangeStep = 'verification';
        this.newEmail = newEmail;
      },
      error: (err) => {
        this.isProcessingEmailChange = false;
        this.isSubmitting = false;
        this.errorMessage = err.message || 'Failed to initiate email change';
      },
    });
  }

  verifyEmailChange(): void {
    if (!this.verificationCode || this.verificationCode.length !== 6) {
      this.errorMessage = 'Please enter the 6-digit verification code';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    this.userService
      .verifyEmailChange(this.newEmail, this.verificationCode)
      .subscribe({
        next: () => {
          this.isProcessingEmailChange = false;
          this.isSubmitting = false;
          this.successMessage = 'Email changed successfully';
          this.editMode = false;
          this.emailChangeStep = 'initial';
          this.verificationCode = '';

          this.authService.forceRefreshUser().subscribe();
        },
        error: (err) => {
          this.isProcessingEmailChange = false;
          this.isSubmitting = false;
          this.errorMessage = err.message || 'Failed to verify email change';
        },
      });
  }

  saveProfileChanges(): void {
    if (this.profileForm.invalid) {
      return;
    }

    const formValues = this.profileForm.value;
    const nonEmailFields: any = {};

    const originalEmail = this.currentUser?.email;
    const newEmail = formValues.email;
    const emailChanged = newEmail !== originalEmail;

    if (emailChanged) {
      this.isProcessingEmailChange = true;
    }

    if (this.currentUser) {
      if (formValues.firstName !== this.currentUser.first_name) {
        nonEmailFields.first_name = formValues.firstName;
      }

      if (formValues.lastName !== this.currentUser.last_name) {
        nonEmailFields.last_name = formValues.lastName;
      }

      if (formValues.country_code && formValues.phone_number) {
        const formattedPhoneNumber = this.formatPhoneNumber();
        if (
          formattedPhoneNumber &&
          formattedPhoneNumber !== this.currentUser.phone_number
        ) {
          nonEmailFields.phone_number = formattedPhoneNumber;
        }
      } else if (!formValues.phone_number && this.currentUser.phone_number) {
        nonEmailFields.phone_number = null;
      }
    }

    const hasNonEmailChanges = Object.keys(nonEmailFields).length > 0;

    if (!hasNonEmailChanges && !emailChanged) {
      this.toggleEditMode();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    if (hasNonEmailChanges) {
      this.userService.updateUserProfile(nonEmailFields).subscribe({
        next: () => {
          if (emailChanged) {
            this.initiateEmailChangeAfterProfileUpdate(newEmail);
          } else {
            this.authService.forceRefreshUser().subscribe({
              next: () => {
                this.isSubmitting = false;
                this.successMessage = 'Profile updated successfully';
                this.editMode = false;
              },
            });
          }
        },
        error: (err) => {
          this.isProcessingEmailChange = false;
          this.isSubmitting = false;
          this.errorMessage = err.message || 'Failed to update profile';
        },
      });
    } else if (emailChanged) {
      this.initiateEmailChange(newEmail);
    }
  }

  private initiateEmailChangeAfterProfileUpdate(newEmail: string): void {
    setTimeout(() => {
      this.userService.initiateEmailChange(newEmail).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.emailChangeStep = 'verification';
          this.newEmail = newEmail;
          this.successMessage =
            'Profile updated successfully. Please check your email to verify your new email address.';
        },
        error: (err) => {
          this.isProcessingEmailChange = false;
          this.isSubmitting = false;
          this.errorMessage =
            err.message ||
            'Profile updated, but failed to initiate email change';
          this.editMode = false;
          this.authService.forceRefreshUser().subscribe();
        },
      });
    }, 100);
  }

  savePasswordChanges(): void {
    if (this.passwordForm.invalid) {
      return;
    }

    const { currentPassword, newPassword } = this.passwordForm.value;

    this.isSubmitting = true;

    this.userService.updatePassword(currentPassword, newPassword).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.successMessage = 'Password updated successfully';
        this.changePasswordMode = false;
        this.passwordForm.reset();
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage = err.message || 'Failed to update password';
      },
    });
  }

  cancelEdit(): void {
    this.isProcessingEmailChange = false;
    this.editMode = false;
    this.changePasswordMode = false;
    this.emailChangeStep = 'initial';
    this.clearMessages();
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }

  closeTopUpModal(): void {
    this.showTopUpModal = false;
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }

  closeEmailPreferencesModal(): void {
    this.showEmailPreferencesModal = false;
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }

  onTopUpComplete(event: {
    balance: number;
    receiptSent?: boolean;
    paymentMethod?: string;
  }): void {
    this.showTopUpModal = false;
    if (this.currentUser) {
      this.currentUser.balance = event.balance;
    }

    if (event.receiptSent) {
      this.successMessage =
        'Balance topped up successfully! A receipt has been sent to your email.';
    } else {
      this.successMessage = 'Balance topped up successfully!';
    }

    this.authService.forceRefreshUser().subscribe();
  }

  formatBalance(balance?: number | string | null): string {
    if (balance === undefined || balance === null) {
      return '$0.00';
    }

    const numBalance =
      typeof balance === 'string' ? parseFloat(balance) : balance;
    return `$${numBalance.toFixed(2)}`;
  }
}
